// Festivals & occasions used to pre-fill the Content Calendar festival editor.
// Users can edit, disable or add their own — this list is only a starting point.

export interface FestivalDay {
  date: string;      // YYYY-MM-DD
  name: string;
  verify?: boolean;  // lunar / moon-sighting dates — check against the local panchang
}

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

// Same date every year (month is 1-indexed)
const FIXED: { m: number; d: number; name: string }[] = [
  { m: 1, d: 1, name: "New Year's Day" },
  { m: 1, d: 12, name: 'National Youth Day' },
  { m: 1, d: 26, name: 'Republic Day' },
  { m: 2, d: 14, name: "Valentine's Day" },
  { m: 3, d: 8, name: "International Women's Day" },
  { m: 4, d: 14, name: 'Ambedkar Jayanti' },
  { m: 5, d: 1, name: 'Labour Day' },
  { m: 6, d: 5, name: 'World Environment Day' },
  { m: 6, d: 21, name: 'International Yoga Day' },
  { m: 7, d: 1, name: "National Doctors' Day" },
  { m: 8, d: 15, name: 'Independence Day' },
  { m: 8, d: 19, name: 'World Photography Day' },
  { m: 9, d: 5, name: "Teachers' Day" },
  { m: 9, d: 27, name: 'World Tourism Day' },
  { m: 10, d: 2, name: 'Gandhi Jayanti' },
  { m: 10, d: 10, name: 'World Mental Health Day' },
  { m: 10, d: 31, name: 'Halloween' },
  { m: 11, d: 1, name: 'Karnataka Rajyotsava' },
  { m: 11, d: 14, name: "Children's Day" },
  { m: 12, d: 25, name: 'Christmas' },
  { m: 12, d: 31, name: "New Year's Eve" },
];

// Lunar / moon-sighting festivals — dates change every year.
const LUNAR: Record<number, FestivalDay[]> = {
  2026: [
    { date: '2026-01-14', name: 'Makar Sankranti / Pongal' },
    { date: '2026-01-23', name: 'Vasant Panchami' },
    { date: '2026-02-15', name: 'Maha Shivaratri' },
    { date: '2026-03-04', name: 'Holi' },
    { date: '2026-03-19', name: 'Ugadi / Gudi Padwa' },
    { date: '2026-03-21', name: 'Eid ul-Fitr' },
    { date: '2026-03-26', name: 'Ram Navami' },
    { date: '2026-04-14', name: 'Baisakhi' },
    { date: '2026-05-01', name: 'Buddha Purnima' },
    { date: '2026-05-27', name: 'Eid ul-Adha (Bakrid)' },
    { date: '2026-07-16', name: 'Rath Yatra' },
    { date: '2026-07-29', name: 'Guru Purnima' },
    { date: '2026-08-26', name: 'Onam' },
    { date: '2026-08-28', name: 'Raksha Bandhan' },
    { date: '2026-09-04', name: 'Krishna Janmashtami' },
    { date: '2026-09-14', name: 'Ganesh Chaturthi' },
    { date: '2026-10-11', name: 'Navratri begins' },
    { date: '2026-10-20', name: 'Dussehra / Vijayadashami' },
    { date: '2026-10-29', name: 'Karva Chauth' },
    { date: '2026-11-06', name: 'Dhanteras' },
    { date: '2026-11-08', name: 'Diwali' },
    { date: '2026-11-11', name: 'Bhai Dooj' },
    { date: '2026-11-24', name: 'Guru Nanak Jayanti' },
  ],
  2027: [
    { date: '2027-01-15', name: 'Makar Sankranti / Pongal' },
    { date: '2027-03-06', name: 'Maha Shivaratri' },
    { date: '2027-03-10', name: 'Eid ul-Fitr' },
    { date: '2027-03-22', name: 'Holi' },
    { date: '2027-04-07', name: 'Ugadi / Gudi Padwa' },
    { date: '2027-04-14', name: 'Baisakhi' },
    { date: '2027-04-15', name: 'Ram Navami' },
    { date: '2027-05-17', name: 'Eid ul-Adha (Bakrid)' },
    { date: '2027-08-17', name: 'Raksha Bandhan' },
    { date: '2027-08-25', name: 'Krishna Janmashtami' },
    { date: '2027-09-04', name: 'Ganesh Chaturthi' },
    { date: '2027-09-12', name: 'Onam' },
    { date: '2027-09-30', name: 'Navratri begins' },
    { date: '2027-10-09', name: 'Dussehra / Vijayadashami' },
    { date: '2027-10-27', name: 'Dhanteras' },
    { date: '2027-10-29', name: 'Diwali' },
    { date: '2027-11-14', name: 'Guru Nanak Jayanti' },
  ],
};

/** nth weekday of a month, e.g. 2nd Sunday of May. weekday: 0 = Sunday. */
function nthWeekday(year: number, month: number, weekday: number, n: number): number {
  const first = new Date(year, month - 1, 1).getDay();
  return 1 + ((weekday - first + 7) % 7) + (n - 1) * 7;
}

/** Western Easter Sunday (anonymous Gregorian algorithm). */
function easter(year: number): Date {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/** Every known festival / occasion in the given month, sorted by date. */
export function getFestivalsForMonth(year: number, month: number): FestivalDay[] {
  const out: FestivalDay[] = FIXED.filter((f) => f.m === month).map((f) => ({ date: iso(year, f.m, f.d), name: f.name }));

  if (month === 5) out.push({ date: iso(year, 5, nthWeekday(year, 5, 0, 2)), name: "Mother's Day" });
  if (month === 6) out.push({ date: iso(year, 6, nthWeekday(year, 6, 0, 3)), name: "Father's Day" });
  if (month === 8) out.push({ date: iso(year, 8, nthWeekday(year, 8, 0, 1)), name: 'Friendship Day' });

  const e = easter(year);
  const gf = new Date(e.getFullYear(), e.getMonth(), e.getDate() - 2);
  if (gf.getMonth() + 1 === month) out.push({ date: iso(year, month, gf.getDate()), name: 'Good Friday' });
  if (e.getMonth() + 1 === month) out.push({ date: iso(year, month, e.getDate()), name: 'Easter' });

  (LUNAR[year] || [])
    .filter((f) => Number(f.date.slice(5, 7)) === month)
    .forEach((f) => out.push({ ...f, verify: true }));

  return out.sort((a, b) => a.date.localeCompare(b.date));
}

/** True when lunar festival dates are known for this year. */
export const hasLunarDates = (year: number) => !!LUNAR[year];
